import './Machine.scss';

import { Col, Collapse, Row } from 'react-bootstrap';
import { LeadingActions, SwipeAction, SwipeableList, SwipeableListItem, TrailingActions } from 'react-swipeable-list';
import React, { useEffect, useMemo, useState } from 'react';
import { faAdd, faCreditCard, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

import AppCard from '../../commons/cards/AppCard';
import AppLoading from '../../commons/AppLoading';
import ConfirmModal from '../../commons/modal/ConfirmModal';
import EmptyState from '../../commons/EmptyState';
import FloatingButton from '../../commons/buttons/FloatingButton';
import FloatingVideoButton from '../../commons/buttons/FloatingVideoButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HallAppConst from '../../commons/util/HallAppConst';
import HallAppToast from '../../commons/HallAppToast';
import MachineAPI from './MachineAPI';
import MenuTitle from '../../commons/menu/MenuTitle';
import ModalMachine from './ModalMachine';
import PullToRefresh from 'react-simple-pull-to-refresh';

const machineAPI = new MachineAPI();

const formatPct = (n) => Number(n || 0).toFixed(2) + '%';
const hasInstallments = (data) =>
  Array.isArray(data?.installmentFees) && data.installmentFees.length > 0;

const Machines = () => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState({});
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);

  // Controle de collapse por card (id -> boolean)
  const [openMap, setOpenMap] = useState({});

  const toggleOpen = (id) =>
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    callGetMachines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callGetMachines = async () => {
    setIsLoading(true);
    try {
      const resp = await machineAPI.findAllMachine();
      setMachines(resp.data || []);
    } catch (e) {
      HallAppToast.error(HallAppConst.GENERIC_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const editAction = (data) => (
    <LeadingActions>
      <SwipeAction
        onClick={() => {
          setMachine(data);
          setShowModal(true);
        }}
        aria-label={`Editar ${data?.name || 'maquininha'}`}
      >
        <div className="swipe">
          <div className="swipe swipe-edt">
            <FontAwesomeIcon icon={faEdit} size="lg" />
            <div>EDITAR</div>
          </div>
        </div>
      </SwipeAction>
    </LeadingActions>
  );

  const deleteAction = (data) => (
    <TrailingActions>
      <SwipeAction
        destructive={false}
        onClick={() => {
          setMachine(data);
          setShowModalDelete(true);
        }}
        aria-label={`Excluir ${data?.name || 'maquininha'}`}
      >
        <div className="swipe">
          <div className="swipe swipe-dlt">
            <FontAwesomeIcon icon={faTrash} size="lg" />
            <div>EXCLUIR</div>
          </div>
        </div>
      </SwipeAction>
    </TrailingActions>
  );

  const deleteMachine = async () => {
    if (!machine?.id) return;
    setIsLoadingModalDelete(true);
    try {
      await machineAPI.deleteMachine(machine.id);
      HallAppToast.success();
      setShowModalDelete(false);
      await callGetMachines();
    } catch (e) {
      HallAppToast.error(HallAppConst.GENERIC_ERROR);
    } finally {
      setIsLoadingModalDelete(false);
    }
  };

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <Col xs={12}>
          <AppLoading />
        </Col>
      );
    }

    if (!machines || machines.length === 0) {
      return (
        <Col xs={12}>
          <EmptyState
            title="Nenhuma maquininha cadastrada"
            description="Adicione suas maquininhas e configure as taxas de Pix, débito e crédito para controlar seus recebimentos."
            actionLabel="Cadastrar maquininha"
            onAction={() => {
              setMachine({});
              setShowModal(true);
            }}
            icon={<FontAwesomeIcon icon={faCreditCard} />}
          />
        </Col>
      );
    }

    return machines.map((data) => {
      const installments = (data.installmentFees || [])
        .slice()
        .sort((a, b) => a.installments - b.installments);

      const isOpen = !!openMap[data.id];

      return (
        <Col xs={12} sm={12} md={4} lg={4} key={data.id} className="mb-3">
          <SwipeableList style={{ width: '100%' }}>
            <SwipeableListItem
              leadingActions={editAction(data)}
              trailingActions={deleteAction(data)}
              key={data.id}
              fullSwipe={false}
            >
              <div style={{ width: '100%' }}>
                <AppCard accent="brand">
                  <div className="machine-card">
                    {/* Header */}
                    <div className="machine-card__header">
                      <div className="machine-card__title">
                        <span className="machine-card__icon" aria-hidden="true">
                          💳
                        </span>
                        <h5 className="mb-0">{data.name}</h5>
                      </div>
                    </div>

                    {/* Taxas principais */}
                    <div className="machine-card__list">
                      <div className="machine-card__row">
                        <span className="machine-card__label">Débito</span>
                        <span className="machine-card__value">
                          {formatPct(data.debitFee)}
                        </span>
                      </div>
                      <div className="machine-card__row">
                        <span className="machine-card__label">Pix</span>
                        <span className="machine-card__value">
                          {formatPct(data.pixFee)}
                        </span>
                      </div>
                      <div className="machine-card__row">
                        <span className="machine-card__label">Crédito (à vista)</span>
                        <span className="machine-card__value">
                          {formatPct(data.creditFee)}
                        </span>
                      </div>

                      {/* Parcelado com collapse */}
                      {hasInstallments(data) && (
                        <>
                          <div className="machine-card__divider" />

                          <button
                            type="button"
                            className="machine-card__collapse-toggle"
                            onClick={() => toggleOpen(data.id)}
                            aria-expanded={isOpen}
                            aria-controls={`installments-${data.id}`}
                          >
                            <span className="machine-card__label" style={{ fontWeight: 600 }}>
                              Crédito parcelado
                            </span>
                            <span className="machine-card__value">
                              {installments.length} opção{installments.length > 1 ? 'es' : ''}
                            </span>
                            <span className={`chevron ${isOpen ? 'is-open' : ''}`} aria-hidden="true">
                              ▾
                            </span>
                          </button>

                          <Collapse in={isOpen}>
                            <div id={`installments-${data.id}`}>
                              <div className="machine-card__installments">
                                {installments.map((item) => (
                                  <div className="machine-card__row" key={item.installments}>
                                    <span className="machine-card__label">{item.installments}x</span>
                                    <span className="machine-card__value">
                                      {formatPct(item.feePercentage)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Collapse>
                        </>
                      )}
                    </div>
                  </div>
                </AppCard>
              </div>
            </SwipeableListItem>
          </SwipeableList>
        </Col>
      );
    });
  }, [isLoading, machines, openMap]);

  return (
    <>
      <PullToRefresh onRefresh={callGetMachines}>
        <div className="container">
          <MenuTitle title="Maquininhas" showDivider />

          <Row style={{ margin: 0, width: '100%' }}>{content}</Row>
        </div>
      </PullToRefresh>

      {/* Modal de confirmação de exclusão */}
      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir maquininha"
          type="danger"
          isLoading={isLoadingModalDelete}
          confirm={deleteMachine}
          closeModal={() => setShowModalDelete(false)}
          textBtnConfirm="Excluir"
          textBtnCancel="Cancelar"
        >
          Deseja remover a maquininha{' '}
          <span style={{ fontWeight: 'bold' }}>{machine?.name}</span>?
        </ConfirmModal>
      )}

      {/* Modal de cadastro/edição */}
      {showModal && (
        <ModalMachine
          callMachines={callGetMachines}
          machine={machine}
          show={showModal}
          closeShow={() => {
            setShowModal(false);
            setMachine({});
          }}
        />
      )}

      <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/o8sfaARVpyM"} />

      {/* FAB para criar */}
      <FloatingButton
        icon={faAdd}
        aria-label="Cadastrar maquininha"
        onClick={() => {
          setMachine({});
          setShowModal(true);
        }}
      />
      <div style={{ height: "calc(50px + 72px)" }}></div>
    </>
  );
};

export default Machines;